import { fromJS, List } from 'immutable'
import isString from 'lodash/isString'
import isArray from 'lodash/isArray'

export const TYPES = {
  COMMENT: 'COMMENT',
  POST: 'POST',
}

// const schema = joi.object({
//   uid: joi.number().required(),
//   type: joi.string().valid(
//     TYPES.COMMENT,
//     TYPES.POST,
//   ).uppercase().required(),
//   value: joi.string().default('').allow('').max(3000),
//   parent: joi.string().required(),
//   data: joi.object().default({}),
//   attachments: joi.object({
//     opengraph: joi.array().items({
//       title: joi.string(),
//       description: joi.string(),
//       url: joi.string().required(),
//       tinyUrl: joi.string(),
//       charset: joi.string(),
//       local: joi.string(),
//       image: joi.object().keys({
//         url: joi.string(),
//         width: joi.string(),
//         height: joi.string(),
//         type: joi.string(),
//       }),
//     }).optional(),
//     // Images are expected to be DATA URL form: 'data:<mediatype>;base64,<data>'
//     // ie. url(data:image/gif;base64,R0lGODlhEAAQAMQAAORHHOVSKudfOulrSOp3CchPGolfO0o/XB...)
//     // https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/Data_URIs
//     images: joi.array().items({
//       data: joi.string().regex(DATA_URI_REGEX).required(),
//       filename: joi.string(),
//     }).optional(),
//   }).optional(),
// })

/**
 * creates an empty draft for activity (post or comment)
 */
export function createDraft (parent = '', type = undefined) {
  return fromJS({
    value: '',
    data: {},
    parent,
    type,
    attachments: {
      images: [],
      files: [],
      og: [],
    },
  })
}

/**
 * updates a draft
 * @param {import('immutable').Map} prev - the prev draft
 * @param {Object} attributes - the attributes
 * @param {String} images - the images
 */
// export function updateDraft ({ prev, attributes, images }) {
//   const { value, data, openGraph, photos } = attributes
//   return (
//     prev.withMutations((d) => {
//       addAttachments(d, 'images', images)
//       setProcessing(d, images && images.length > 0)
//       setValue(d, value)
//       setData(d, data)
//     })
//   )
// }

/**
 * sets draft value
 * @param {import('immutable').Map} draft - the draft
 * @param {String} value - the value
 */
export function setValue (draft, value) {
  return isString(value)
    ? draft.set('value', value)
    : draft
}

/**
 * sets a draft primary image
 * @param {import('immutable').Map} draft - the draft
 * @param {String} image - the image src
 */
export function setImage (draft, image) {
  return image && isString(image)
    && /http(s)?:\/\//.test(image)
    ? draft.set('image', image)
    : draft
}

/**
 * sets the draft object property based on current values (activity)
 * @param {import('immutable').Map} draft - the draft
 */
export function setObject (draft) {
  const text = draft.get('text')
  const image = draft.get('image')
  const graphs = draft.getIn(['attachments', 'og'], List())
  const graph = graphs.find(g => g.get('valid') && g.get('render'))
  const photo = draft.getIn(['attachments', 'images', 0, 'src'])
  const file = draft.getIn(['attachments', 'files', 0, 'src'])
  return draft.set('object', (
    text || image || (graph && graph.get('url')) || photo || file
  ))
}

/**
 * sets the draft data property based on current values
 * @param {import('immutable').Map} draft - the draft
 */
export function setData (draft) {
  return draft.set('data', (
    { text: draft.get('text') }
  ))
}

/**
 * sets a drafts processing state
 * @param {import('immutable').Map} draft - the draft
 * @param {Boolean} bool - processing flag
 */
export function setProcessing (draft, bool) {
  return draft.set('processing', !!bool)
}

/**
 * sets processing state for photos
 * @param {import('immutable').Map} draft - the draft
 */
export function setPhotosProcessing (draft) {
  const images = draft.getIn(['attachments', 'images'], List())
  const complete = images.every(img => (
    img.get('src') || img.get('error')
  ))
  return draft.set(
    'processing', !complete)
}

/**
 * deletes an attachment from draft
 * @param {import('immutable').Map} draft - the draft
 * @param {String} key - attachment key
 * @param {String} id - the attachment id
 */
export function deleteAttachment (draft, key, id) {
  if (id) {
    return draft.updateIn(
      ['attachments', key], List(), files => (
        files.filter(f => f.get('id') !== id)
      ))
  }
  return draft
}

/**
 * sets draft attachments
 * @param {import('immutable').Map} draft - the draft
 * @param {String} key - attachment key
 * @param {Object[]} attachments - the attachments
 */
export function setAttachments (draft, key, attachments) {
  //
  let list = attachments || []

  if (isArray(list)) {
    list = fromJS(list)
  }
  return draft.setIn(
    ['attachments', key], list)
}

/**
 * adds draft attachments
 * @param {import('immutable').Map} draft - the draft
 * @param {String} key - attachment key
 * @param {Object[]} attachments - the attachments
 */
export function addAttachments (draft, key, attachments) {
  //
  let list = attachments || []

  if (isArray(list)) {
    list = fromJS(list)
  }

  if (list.size) {
    return draft.updateIn(
      ['attachments', key], l => (
        l.concat(list)
      ))
  }
  return draft
}

/**
 * merges data onto existing draft attachments
 * @param {import('immutable').Map} draft - the draft
 * @param {String} key - attachment key
 * @param {Object[]} attachments - the attachments
 */
export function mergeAttachments (draft, key, attachements) {
  //
  let data = attachements

  if (isArray(data)) {
    data = fromJS(data)
  }

  return draft.updateIn(['attachments', key], (list) => {
    return list.map((item) => {
      const id = item.get('id')
      const attachment = data.find(d => d.get('id') === id)
      return attachment
        ? item.merge(attachment)
        : item
    })
  })
}
